import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import {
  MenuItem,
  Paper,
  IconButton,
  Box,
  Tooltip,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  TextField,
  InputAdornment,
  FormControl,
  Pagination,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import EmailIcon from "@mui/icons-material/Email";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import EditIcon from "@mui/icons-material/Edit";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SearchIcon from "@mui/icons-material/Search";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import FileDialog from "components/FileDialog";

import useUserRoles from "hooks/useUserRoles";
import { getCommunicationStatus, getErrorDetails } from "utils/comunicacionStatus";

import ComunicacionesApi from "api/comunicacionesApi";
import BucketApi from "api/bucketApi";
import DatosBasicosApi from "api/datosbasicosApi";
import DialogDestinatarios from "../../resumen/components/dialogDestinatarios";
import NotificarComunicacion from "../../resumen/components/notificarComunicacion";
import CreateComunicacion from "./createComunicacion";
import InformeEstadoProceso from "./informeEstadoProceso";
import EditComunicacion from "./editComunicacion";

const ComunicacionesTable = () => {
  const { user, roles } = useUserRoles();

  /**
   * Parsea una fecha en formato "YYYY-MM-DD" a Date local sin problemas de timezone.
   * Evita el problema de interpretación UTC que causa que las fechas se muestren un día antes.
   *
   * @param {string} dateString - Fecha en formato "YYYY-MM-DD"
   * @returns {Date|null} - Objeto Date en hora local o null si no es válida
   */
  const parseDateLocal = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day); // month - 1 porque los meses son 0-indexed en JS
  };

  // Estados para paginación del servidor
  const [comunicaciones, setComunicaciones] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(0);

  // Estados para filtros del servidor
  const [searchTerm, setSearchTerm] = useState("");
  const [tipoFiltro, setTipoFiltro] = useState("");
  const [formatoFiltro, setFormatoFiltro] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("");
  const [procesoEstadoFiltro, setProcesoEstadoFiltro] = useState("");

  // Estados para opciones de filtros
  const [estadosDisponibles, setEstadosDisponibles] = useState([]);

  // Estados generales
  const [loading, setLoading] = useState(true);

  // Estados del menú y modales
  const [selectedComunicacion, setSelectedComunicacion] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDialogNotificar, setOpenDialogNotificar] = useState(false);
  const [openNewDialog, setOpenNewDialog] = useState(false);
  const [openInformeEstado, setOpenInformeEstado] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);

  // Estados para manejar PDFs y comprobantes
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfTitle, setPdfTitle] = useState("");
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [tipoArchivo, setTipoArchivo] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [fileUploading, setFileUploading] = useState(false);

  const hasFetched = useRef(false);
  const searchTimeoutRef = useRef(null);
  // Identificador de la última petición: evita que una respuesta tardía sobrescriba una más reciente
  const ultimaPeticionRef = useRef(0);
  // Efectos que aún no han pasado por su ejecución de montaje
  const efectosPendientesMontaje = useRef(
    new Set(["pagina", "busqueda", "filtros", "tamanoPagina"])
  );
  const omitirEnMontaje = (efecto) => efectosPendientesMontaje.current.delete(efecto);

  // FUNCIÓN PRINCIPAL PARA CARGAR DATOS - SIN DEPENDENCIAS DE ESTADO
  const fetchComunicaciones = useCallback(
    async (page, pageSize, search, tipo, formato, estado, procesoEstado) => {
      console.log(`📄 Cargando comunicaciones - Página: ${page}`);
      const peticionId = ++ultimaPeticionRef.current;
      setLoading(true);
      try {
        const params = { page, page_size: pageSize };
        // Solo agregar parámetros no vacíos
        if (search && search.trim()) params.search = search.trim();
        if (tipo) params.tipo_comunicacion = tipo;
        if (formato) params.formato_comunicacion = formato;
        if (estado) params.estado = estado;
        if (procesoEstado) params.proceso_estado = procesoEstado;
        const response = await ComunicacionesApi.fetchComunicacionesPaginacion(params);
        if (peticionId !== ultimaPeticionRef.current) return; // Respuesta obsoleta
        setComunicaciones(response.data.results);
        setTotalItems(response.data.pagination.total_items);
        setTotalPages(response.data.pagination.total_pages);
      } catch (error) {
        if (peticionId !== ultimaPeticionRef.current) return; // Respuesta obsoleta
        console.error("Error al obtener las Comunicaciones:", error);
        setComunicaciones([]);
        setTotalItems(0);
        setTotalPages(0);
      } finally {
        if (peticionId === ultimaPeticionRef.current) setLoading(false);
      }
    },
    []
  );

  // Cargar filtros disponibles
  const cargarFiltros = useCallback(async () => {
    try {
      const estadosResponse = await DatosBasicosApi.fetchEstadosProceso();
      setEstadosDisponibles(estadosResponse.data);
    } catch (error) {
      console.error("Error al cargar filtros:", error);
    }
  }, []);

  // Effect SOLO para carga inicial
  useEffect(() => {
    if (user && !hasFetched.current) {
      console.log("🚀 Carga inicial");
      hasFetched.current = true;
      cargarFiltros();
      fetchComunicaciones(
        1,
        pageSize,
        searchTerm,
        tipoFiltro,
        formatoFiltro,
        estadoFiltro,
        procesoEstadoFiltro
      );
    }
  }, [
    user,
    roles,
    cargarFiltros,
    fetchComunicaciones,
    pageSize,
    searchTerm,
    tipoFiltro,
    formatoFiltro,
    estadoFiltro,
    procesoEstadoFiltro,
  ]);

  // Effect SOLO para cambios de página (navegación)
  useEffect(() => {
    if (omitirEnMontaje("pagina")) return;
    if (hasFetched.current) {
      console.log(`📄 Navegando a página: ${currentPage}`);
      fetchComunicaciones(
        currentPage,
        pageSize,
        searchTerm,
        tipoFiltro,
        formatoFiltro,
        estadoFiltro,
        procesoEstadoFiltro
      );
    }
  }, [currentPage]);

  // Effect para búsqueda con debounce
  useEffect(() => {
    if (omitirEnMontaje("busqueda")) return;
    if (!hasFetched.current) return;

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      console.log(`🔍 Búsqueda: "${searchTerm}"`);
      setCurrentPage(1);
      fetchComunicaciones(
        1,
        pageSize,
        searchTerm,
        tipoFiltro,
        formatoFiltro,
        estadoFiltro,
        procesoEstadoFiltro
      );
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Effect para filtros
  useEffect(() => {
    if (omitirEnMontaje("filtros")) return;
    if (hasFetched.current) {
      console.log("🎛️ Aplicando filtros");
      setCurrentPage(1);
      fetchComunicaciones(
        1,
        pageSize,
        searchTerm,
        tipoFiltro,
        formatoFiltro,
        estadoFiltro,
        procesoEstadoFiltro
      );
    }
  }, [tipoFiltro, formatoFiltro, estadoFiltro, procesoEstadoFiltro]);

  // Effect para cambios de pageSize
  useEffect(() => {
    if (omitirEnMontaje("tamanoPagina")) return;
    if (hasFetched.current) {
      console.log(`📊 Cambiando tamaño de página: ${pageSize}`);
      setCurrentPage(1);
      fetchComunicaciones(
        1,
        pageSize,
        searchTerm,
        tipoFiltro,
        formatoFiltro,
        estadoFiltro,
        procesoEstadoFiltro
      );
    }
  }, [pageSize]);

  // Handlers para filtros del servidor
  const handleTipoChange = (event) => {
    setTipoFiltro(event.target.value);
  };

  const handleFormatoChange = (event) => {
    setFormatoFiltro(event.target.value);
  };

  const handleProcesoEstadoChange = (event) => {
    setProcesoEstadoFiltro(event.target.value);
  };

  const handlePageChange = (event, newPage) => {
    console.log(`👆 Usuario hace clic en página: ${newPage}`);
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  const limpiarFiltros = () => {
    setSearchTerm("");
    setTipoFiltro("");
    setFormatoFiltro("");
    setEstadoFiltro("");
    setProcesoEstadoFiltro("");
    setCurrentPage(1);
  };

  // Función para recargar manteniendo página actual
  const reloadCurrentPage = () => {
    fetchComunicaciones(
      currentPage,
      pageSize,
      searchTerm,
      tipoFiltro,
      formatoFiltro,
      estadoFiltro,
      procesoEstadoFiltro
    );
  };

  // Verifica si todos los destinatarios de la comunicación han sido notificados
  const isFullyNotified = (comunicacion) => {
    if (!comunicacion || !comunicacion.destinatarios) return false;
    const { destinatarios } = comunicacion;
    // Para comunicaciones físicas
    if (comunicacion.tipo_comunicacion === "FISICA") {
      if (destinatarios.acreedor) {
        return destinatarios.acreedor.notificado === true;
      }
      return false;
    } else if (comunicacion.tipo_comunicacion === "EMAIL") {
      // Para comunicaciones: notificacionManual
      if (comunicacion.formato_comunicacion === "notificacionManual") {
        if (Array.isArray(destinatarios)) {
          return destinatarios.every((d) => d.notificado === true);
        }
        return false;
      }
      // Para comunicaciones por email - Verificar deudor
      if (destinatarios.deudor && !destinatarios.deudor.notificado) return false;
      // Verificar apoderado del deudor
      if (destinatarios.deudor_apoderado && !destinatarios.deudor_apoderado.notificado)
        return false;
      // Verificar centrales de riesgo
      if (destinatarios.centrales_riesgo && destinatarios.centrales_riesgo.length > 0) {
        if (!destinatarios.centrales_riesgo.every((central) => central.notificado)) return false;
      }
      // Verificar acreedores
      if (destinatarios.acreedores && destinatarios.acreedores.length > 0) {
        if (!destinatarios.acreedores.every((acreedor) => acreedor.notificado)) return false;
      }
      return true;
    }
  };

  const handleOpenDialog = (comunicacion) => {
    setSelectedComunicacion(comunicacion);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedComunicacion(null);
  };

  const handleOpenNotificar = (comunicacion) => {
    setSelectedComunicacion(comunicacion);
    setOpenDialogNotificar(true);
  };

  const handleCloseDialogNotificar = () => {
    setOpenDialogNotificar(false);
    setSelectedComunicacion(null);
  };

  const handleOpenEdit = (comunicacion) => {
    setSelectedComunicacion(comunicacion);
    setOpenEditDialog(true);
  };

  const handleOpenUploadDialog = (comunicacion) => {
    setSelectedComunicacion(comunicacion);
    setOpenUploadDialog(true);
  };

  const handleNotificacionSuccess = async () => {
    try {
      reloadCurrentPage();
      setOpenDialogNotificar(false);
      setSelectedComunicacion(null);
    } catch (error) {
      console.error("Error actualizando comunicaciones después de notificación:", error);
    }
  };

  const handleFileChange = (event) => {
    setUploadFile(event.target.files[0]);
  };

  const handleUploadArchivo = async () => {
    if (tipoArchivo == "") {
      alert("Seleccione un Tipo de Archivo");
      return;
    }
    if (!uploadFile) {
      alert("Seleccione un Archivo");
      return;
    }
    let allowedExtensions = ["pdf"];
    const extension = uploadFile.name.split(".").pop().toLowerCase();
    if (!allowedExtensions.includes(extension)) {
      alert("Sólo se pueden anexar archivos con extensión PDF.");
      return;
    }
    try {
      setFileUploading(true);
      let fileName = `${tipoArchivo}.${extension}`;
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("file_name", fileName);
      formData.append("file_type", "comunicaciones");
      formData.append("proceso_id", selectedComunicacion.proceso_identificador);
      formData.append("comunicacion_id", selectedComunicacion.id);
      const response = await BucketApi.uploadFile(formData);
      if (response.data && response.data.file_url) {
        if (
          tipoArchivo == "comprobante_envio" &&
          selectedComunicacion.tipo_comunicacion == "FISICA"
        ) {
          const destinatariosActualizados = selectedComunicacion.destinatarios.map((d) => ({
            ...d,
            notificado: true,
          }));
          const destinatariosSeleccionados = {};
          destinatariosActualizados.forEach((_, idx) => {
            destinatariosSeleccionados[`acreedor_${idx}`] = true;
          });
          const datos = {
            operacion: "NOTIFICAR_COMUNICACION",
            id_comunicacion: selectedComunicacion.id,
            destinatarios_seleccionados: destinatariosSeleccionados,
          };
          await ComunicacionesApi.operarComunicacion(datos);
        }
        alert("Archivo subido correctamente.");
        reloadCurrentPage();
        setOpenUploadDialog(false);
        setUploadFile(null);
      } else {
        throw new Error("No se pudo obtener la URL del archivo subido");
      }
    } catch (error) {
      console.error("Error al subir el Archivo:", error);
      alert(obtenerMensajeError(error, "Error al subir el Archivo. Intente nuevamente."));
    } finally {
      setFileUploading(false);
    }
  };

  const handleViewPdf = async (tipo_archivo, comunicacion) => {
    if (!tipo_archivo) return;
    if (tipo_archivo == "comprobante_envio") {
      setPdfTitle(`Comprobante de Envío`);
    } else {
      setPdfTitle(`Anexo`);
    }
    let fileName = `${tipo_archivo}.pdf`;
    let urlResponse = await BucketApi.getComunicacionFileUrlFresh(
      fileName,
      comunicacion.proceso_identificador,
      comunicacion.id
    );
    if (urlResponse.data.exists) {
      setPdfUrl(urlResponse.data.file_url);
      setShowPdfViewer(true);
    } else {
      alert("El PDF no ha sido cargado.");
    }
    return;
  };

  const handleViewPdfNotificacion = async (comunicacion) => {
    if (!comunicacion) return;
    // Limpiar URL anterior si existe
    if (pdfUrl && pdfUrl.startsWith("blob:")) {
      URL.revokeObjectURL(pdfUrl);
    }
    try {
      setLoading(true);
      const timestamp = Date.now();
      const pdfResponse = await ComunicacionesApi.generateComunicacionPdf(
        comunicacion.id,
        { responseType: "blob" },
        timestamp
      );
      const pdfBlob = new Blob([pdfResponse.data], { type: "application/pdf" });
      const url = URL.createObjectURL(pdfBlob);
      // Crear una URL única forzando re-render
      const uniqueUrl = `${url}#${timestamp}`;
      setPdfTitle(`Notificación - ${comunicacion.formato_comunicacion}`);
      setPdfUrl(uniqueUrl);
      setShowPdfViewer(true);
    } catch (error) {
      console.error("Error generando PDF de notificación:", error);
      alert(obtenerMensajeError(error, "Error al generar el PDF de notificación"));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenNewDialog = () => {
    setOpenNewDialog(true);
  };

  const handleActualizarDestinatarios = async (comunicacion) => {
    try {
      const response = await ComunicacionesApi.obtenerDestinatarios(comunicacion.id);
      if (!response.data) {
        alert("No se pudieron obtener los destinatarios actualizados");
        return;
      }
      const confirmAction = window.confirm(
        `¿Estás seguro(a) que deseas actualizar los Destinatarios de esta Comunicación?\n\n` +
          `Proceso: ${comunicacion.proceso_identificador}\n` +
          `Asunto: ${comunicacion.asunto}`
      );
      if (!confirmAction) return;
      const dataToUpdate = {
        proceso: comunicacion.proceso_id,
        audiencia: comunicacion.audiencia_id,
        tipo_comunicacion: comunicacion.tipo_comunicacion,
        formato_comunicacion: comunicacion.formato_comunicacion,
        asunto: comunicacion.asunto,
        contenido: comunicacion.contenido || "",
        destinatarios: response.data, // Nuevos destinatarios
        creado_por: comunicacion.creado_por,
      };
      try {
        setLoading(true);
        await ComunicacionesApi.actualizarComunicacion(comunicacion.id, dataToUpdate);
        alert("Destinatarios actualizados correctamente");
        reloadCurrentPage();
      } catch (error) {
        console.error("Error actualizando los destinatarios:", error);
        alert(obtenerMensajeError(error, "Error al actualizar los destinatarios"));
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error al actualizar destinatarios:", error);
      const errorMsg = error.response?.data?.error || error.message || "Error desconocido";
      alert(`Error al actualizar los Destinatarios:\n${errorMsg}`);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("¿Está seguro(a) de eliminar esta Comunicación?")) {
      try {
        setLoading(true);
        await ComunicacionesApi.eliminarComunicacion(id);
        reloadCurrentPage();
      } catch (error) {
        console.error("Error al eliminar la Comunicación:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const renderGridActions = (params) => {
    const comunicacion = params.row;
    return (
      <>
        <Box display="flex" flexWrap="wrap" gap={0.5}>
          {/* Primera fila de botones */}
          {comunicacion.formato_comunicacion !== "notificacionManual" &&
            comunicacion.formato_comunicacion !== "informeEstadoProceso" && (
              <Tooltip title="Actualizar Destinatarios">
                <IconButton
                  size="small"
                  onClick={() => handleActualizarDestinatarios(comunicacion)}
                  color="info"
                >
                  <AutorenewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          <Tooltip title="Ver Destinatarios">
            <IconButton size="small" onClick={() => handleOpenDialog(comunicacion)} color="info">
              <AlternateEmailIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar Comunicación">
            <IconButton size="small" onClick={() => handleDelete(comunicacion.id)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {comunicacion.formato_comunicacion === "notificacionManual" && (
            <Tooltip title="Editar Comunicación">
              <IconButton size="small" onClick={() => handleOpenEdit(comunicacion)} color="primary">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Subir Archivo">
            <IconButton
              size="small"
              onClick={() => handleOpenUploadDialog(comunicacion)}
              color="info"
            >
              <UploadFileIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box display="flex" flexWrap="wrap" gap={0.5} maxWidth={150}>
          {/* segunda fila */}
          {comunicacion.tipo_comunicacion === "FISICA" && (
            <Tooltip title="Ver Comprobante Envío">
              <IconButton
                size="small"
                onClick={() => handleViewPdf("comprobante_envio", comunicacion)}
                color="primary"
              >
                <MarkEmailReadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Ver Anexo">
            <IconButton
              size="small"
              onClick={() => handleViewPdf("anexo_correo", comunicacion)}
              color="primary"
            >
              <AttachFileIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Ver Notificación">
            <IconButton
              size="small"
              onClick={() => handleViewPdfNotificacion(comunicacion)}
              color="success"
            >
              <FilePresentIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {comunicacion.tipo_comunicacion === "EMAIL" && (
            <Tooltip title="Notificar Comunicación">
              <IconButton
                size="small"
                onClick={() => handleOpenNotificar(comunicacion)}
                color="secondary"
              >
                <ReplyRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </>
    );
  };

  const columns = [
    {
      field: "Opciones",
      renderHeader: () => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <AddCircleIcon onClick={handleOpenNewDialog} fontSize="large" />
        </Box>
      ),
      width: 150,
      sortable: false,
      align: "center",
      disableColumnMenu: true,
      resizable: false,
      renderCell: renderGridActions,
    },
    {
      field: "cliente",
      headerName: "Deudor - Proceso",
      flex: 2,
      renderCell: (params) => (
        <Tooltip title={params.row.id}>
          <SuiTypography variant="caption" fontWeight="medium">
            {params.row.cliente_nombre || ""} - {params.row.proceso_identificador || ""}
          </SuiTypography>
        </Tooltip>
      ),
    },
    {
      field: "asunto",
      headerName: "Asunto",
      flex: 4,
      renderCell: (params) => (
        <>
          <div>
            <Box display="center" alignItems="center">
              <Tooltip title={params.row.tipo_comunicacion}>
                {params.row.tipo_comunicacion === "EMAIL" ? (
                  <EmailIcon color="info" fontSize="medium" sx={{ mr: 1 }} />
                ) : (
                  <LocalShippingIcon color="primary" fontSize="medium" sx={{ mr: 1 }} />
                )}
              </Tooltip>
              <SuiTypography variant="caption" fontWeight="medium" component="span">
                {params.row.asunto}
              </SuiTypography>
              <br />
              <span style={{ fontSize: "90%", marginLeft: "4px", color: "grey" }}>
                ({params.row.formato_comunicacion})
              </span>
            </Box>
          </div>
        </>
      ),
    },
    {
      field: "alarma_notificacion",
      headerName: "Notificación",
      width: 120,
      //flex: 1,
      sortable: true,
      align: "center",
      disableColumnMenu: true,
      resizable: false,
      renderCell: (params) => {
        const comunicacion = params.row;
        const estadoNotificacion = comunicacion.estado_notificacion;
        const diasParaNotificar = comunicacion.dias_para_notificar;
        const fechaAudiencia = comunicacion.fecha_audiencia;

        // Si no hay audiencia vinculada
        if (estadoNotificacion === "SIN_AUDIENCIA") {
          return (
            <Tooltip title="Sin audiencia asociada">
              <Box display="flex" alignItems="center" justifyContent="center">
                <EventBusyIcon sx={{ color: "#9e9e9e", fontSize: 30 }} />
                <span style={{ marginLeft: 4, fontSize: "1rem", color: "#9e9e9e" }}>N/A</span>
              </Box>
            </Tooltip>
          );
        }

        // Configuración de estilos por estado
        const estadoConfig = {
          VENCIDO: {
            icon: <ErrorIcon sx={{ color: "#f44336" }} />,
            bgColor: "#ffebee",
            textColor: "#c62828",
            texto: `${Math.abs(diasParaNotificar)} días vencida`,
            tooltip: `Ya pasó la fecha límite de notificación`,
          },
          CRITICO: {
            icon: <WarningIcon sx={{ color: "#ff5722" }} />,
            bgColor: "#fbe9e7",
            textColor: "#d84315",
            texto: "HOY",
            tooltip: "¡Último día para notificar!",
          },
          URGENTE: {
            icon: <WarningIcon sx={{ color: "#ff9800" }} />,
            bgColor: "#fff3e0",
            textColor: "#ef6c00",
            texto: `${diasParaNotificar} días`,
            tooltip: `Faltan ${diasParaNotificar} días para notificar`,
          },
          ADVERTENCIA: {
            icon: <AccessTimeIcon sx={{ color: "#ffc107" }} />,
            bgColor: "#fffde7",
            textColor: "#f57f17",
            texto: `${diasParaNotificar} días`,
            tooltip: `Faltan ${diasParaNotificar} días para notificar`,
          },
          NORMAL: {
            icon: <CheckCircleIcon sx={{ color: "#4caf50" }} />,
            bgColor: "#e8f5e8",
            textColor: "#2e7d32",
            texto: `${diasParaNotificar} días`,
            tooltip: `Faltan ${diasParaNotificar} días para notificar`,
          },
        };
        const config = estadoConfig[estadoNotificacion];
        if (!config) return null;
        return (
          <Tooltip
            title={`${config.tooltip}${
              fechaAudiencia
                ? ` - Audiencia: ${parseDateLocal(fechaAudiencia).toLocaleDateString("es-ES")}`
                : ""
            }`}
            arrow
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                backgroundColor: config.bgColor,
                color: config.textColor,
                padding: "2px 4px",
                borderRadius: "8px",
                minWidth: "50px",
                border:
                  estadoNotificacion === "VENCIDO" || estadoNotificacion === "CRITICO"
                    ? "1px solid currentColor"
                    : "none",
              }}
            >
              {config.icon}
              <span
                style={{
                  marginLeft: 3,
                  fontSize: "0.75rem",
                  fontWeight:
                    estadoNotificacion === "VENCIDO" || estadoNotificacion === "CRITICO"
                      ? "bold"
                      : "medium",
                }}
              >
                {config.texto}
              </span>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "estado",
      headerName: "Estado",
      flex: 1,
      renderCell: (params) => {
        const comunicacion = params.row;
        let estado = comunicacion.estado || "PENDIENTE";
        // Si todos los destinatarios están notificados, mostrar como ENVIADO/HECHO
        if (isFullyNotified(comunicacion)) {
          estado = comunicacion.tipo_comunicacion === "FISICA" ? "ENVIADO" : "HECHO";
        }
        const estadoStyles = {
          PENDIENTE: { backgroundColor: "#9e9e9e", color: "white" },
          ENVIADO: { backgroundColor: "#43A047", color: "white" },
          GENERADO: { backgroundColor: "#FB8C00", color: "white" },
          ERROR: { backgroundColor: "#E53935", color: "white" },
          HECHO: { backgroundColor: "#43A047", color: "white" },
          PARCIAL: { backgroundColor: "#FB8C00", color: "white" },
        };
        const style = estadoStyles[estado] || { backgroundColor: "gray", color: "white" };
        return (
          <Tooltip title={estado}>
            <div
              style={{
                display: "inline-block",
                backgroundColor: style.backgroundColor,
                color: style.color,
                padding: "4px 12px",
                borderRadius: "12px",
                fontWeight: "bold",
                fontSize: "0.7rem",
                textAlign: "center",
                minWidth: "80px",
              }}
            >
              {estado}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "email_status",
      headerName: "Email",
      width: 100,
      sortable: false,
      align: "center",
      disableColumnMenu: true,
      resizable: false,
      renderCell: (params) => {
        const comunicacion = params.row;
        // Solo mostrar para comunicaciones EMAIL
        if (comunicacion.tipo_comunicacion !== "EMAIL") {
          return <span style={{ color: "#9e9e9e" }}>N/A</span>;
        }
        const status = getCommunicationStatus(comunicacion);
        const errors = getErrorDetails(comunicacion.errores_envio || []);
        let tooltipText = status.tooltip;
        if (errors.length > 0) {
          tooltipText +=
            "\n\nErrores:\n" + errors.map((e) => `• ${e.email}: ${e.descripcion}`).join("\n");
        }
        return (
          <Tooltip title={tooltipText} arrow>
            <span
              style={{
                fontSize: "20px",
                color: status.color,
                cursor: "help",
              }}
            >
              {status.icon}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "fecha_creacion",
      headerName: "Creación",
      flex: 1,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.fecha_creacion
            ? new Date(params.row.fecha_creacion).toLocaleString("es-ES", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "N/A"}
        </SuiTypography>
      ),
    },
    {
      field: "fecha_envio",
      headerName: "Envío",
      flex: 1,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.fecha_envio
            ? new Date(params.row.fecha_envio).toLocaleString("es-ES", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "Pendiente"}
        </SuiTypography>
      ),
    },
  ];

  return (
    <SuiBox>
      {loading && (
        <Backdrop open={true} style={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          boxShadow: "none",
          backgroundColor: "transparent",
        }}
      >
        {/* Header con controles */}
        <SuiBox p={1}>
          <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mb: 1 }}>
            <Grid size={6}>
              <SuiTypography variant="h4" fontWeight="bold">
                Administración de Comunicaciones ({totalItems})
              </SuiTypography>
            </Grid>
            {(roles.isNotificador || roles.isAdmin) && (
              <Grid size={6}>
                <SuiBox display="flex" justifyContent="flex-end">
                  <SuiButton
                    variant="gradient"
                    buttonColor="dark"
                    size="small"
                    onClick={() => setOpenInformeEstado(true)}
                  >
                    Informe Estado del Proceso
                  </SuiButton>
                </SuiBox>
              </Grid>
            )}
          </Grid>
          <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              <SuiBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 350 }}>
                  <SuiTypography variant="h6" fontWeight="light">
                    Buscar por...
                  </SuiTypography>
                  <TextField
                    size="small"
                    placeholder="identificador, cliente, asunto..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ minWidth: 400 }}
                  />
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <SuiTypography variant="h6" fontWeight="light">
                    Tipo
                  </SuiTypography>
                  <Select value={tipoFiltro} label="Tipo" onChange={handleTipoChange}>
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="EMAIL">EMAIL</MenuItem>
                    <MenuItem value="FISICA">FISICA</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <SuiTypography variant="h6" fontWeight="light">
                    Formato
                  </SuiTypography>
                  <Select value={formatoFiltro} label="Formato" onChange={handleFormatoChange}>
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="notificacionAdmision">Notif. Admisión</MenuItem>
                    <MenuItem value="notificacionNuevaAudiencia">Nueva Audiencia</MenuItem>
                    <MenuItem value="notificacionManual">Manual</MenuItem>
                    <MenuItem value="informeEstadoProceso">Informe Estado</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <SuiTypography variant="h6" fontWeight="light">
                    Estado Proceso
                  </SuiTypography>
                  <Select
                    value={procesoEstadoFiltro}
                    label="Estado Proceso"
                    onChange={handleProcesoEstadoChange}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {estadosDisponibles.map((estado) => (
                      <MenuItem key={estado.id} value={estado.id}>
                        {estado.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <SuiTypography variant="h6" fontWeight="light">
                    Items x Página
                  </SuiTypography>
                  <Select
                    value={pageSize}
                    label="Tamaño"
                    onChange={(e) => handlePageSizeChange(e.target.value)}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
                <Tooltip title="Limpiar Filtros" placement="right">
                  <IconButton
                    onClick={limpiarFiltros}
                    disabled={loading}
                    size="large"
                    sx={{ ml: 1 }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </SuiBox>
            </Grid>
          </Grid>
        </SuiBox>

        {/* DataGrid */}
        <DataGrid
          rows={comunicaciones}
          columns={columns}
          loading={loading}
          getRowHeight={() => "auto"}
          sx={{
            border: 0,
            backgroundColor: "white",
            borderRadius: 2,
            [`& .${gridClasses.cell}`]: {
              borderBottom: "none",
              padding: "2px 2px",
            },
            [`& .${gridClasses.row}`]: {
              backgroundColor: ({ palette: { white } }) => white.main,
              "&:hover": {
                backgroundColor: ({ palette: { grey } }) => grey[200],
              },
              minHeight: "48px !important",
            },
            [`& .${gridClasses.columnHeaders}`]: {
              backgroundColor: ({ palette: { grey } }) => grey[50],
              borderRadius: 1,
              minHeight: "48px !important",
            },
            [`& .${gridClasses.footerContainer}`]: {
              display: "none",
            },
          }}
          hideFooter
        />

        {/* Paginación del servidor */}
        <SuiBox display="flex" justifyContent="center" mt={2} mb={1}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            siblingCount={1}
            boundaryCount={1}
          />
        </SuiBox>
      </Paper>

      {/* Diálogo para ver destinatarios */}
      <DialogDestinatarios
        open={openDialog}
        onClose={handleCloseDialog}
        comunicacion={selectedComunicacion}
      />
      <NotificarComunicacion
        open={openDialogNotificar}
        onClose={handleCloseDialogNotificar}
        comunicacion={selectedComunicacion}
        onSuccess={handleNotificacionSuccess}
      />

      {/* Visor de PDF */}
      <FileDialog
        open={showPdfViewer}
        onClose={() => {
          setShowPdfViewer(false);
          if (pdfUrl && pdfUrl.startsWith("blob:")) {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl("");
          }
        }}
        title={pdfTitle}
        content={
          <iframe
            src={pdfUrl}
            width="100%"
            height="500px"
            style={{ border: "none" }}
            key={pdfUrl}
            onLoad={() => console.log("PDF cargado:", pdfUrl)}
          />
        }
      />

      <InformeEstadoProceso
        open={openInformeEstado}
        onClose={() => setOpenInformeEstado(false)}
        onSuccess={(comunicacion) => {
          setOpenInformeEstado(false);
          reloadCurrentPage();
          // Abrir el envío a los juzgados con la comunicación recién creada
          handleOpenNotificar({
            ...comunicacion,
            proceso_id: comunicacion.proceso,
            audiencia_id: comunicacion.audiencia,
          });
        }}
      />
      <CreateComunicacion
        open={openNewDialog}
        onClose={() => setOpenNewDialog(false)}
        onSuccess={() => {
          reloadCurrentPage();
          setOpenNewDialog(false);
        }}
      />
      <EditComunicacion
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        comunicacion={selectedComunicacion}
        onSuccess={() => {
          reloadCurrentPage();
          setOpenEditDialog(false);
        }}
      />

      {/* Diálogo para subir archivos */}
      <Dialog
        open={openUploadDialog}
        onClose={() => !fileUploading && setOpenUploadDialog(false) && setUploadFile(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Subir Archivo</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <SuiTypography variant="body2" component="p">
              Tipo Archivo
            </SuiTypography>
            <Select
              value={tipoArchivo}
              onChange={(e) => setTipoArchivo(e.target.value)}
              displayEmpty
              fullWidth
            >
              <MenuItem value="">Seleccione un Tipo de Archivo</MenuItem>
              <MenuItem value="anexo_correo">Anexo (PDF)</MenuItem>
              {selectedComunicacion?.tipo_comunicacion === "FISICA" && (
                <MenuItem value="comprobante_envio">Comprobante de Envio (PDF)</MenuItem>
              )}
            </Select>
          </Box>
          <Box border="1px dashed #bdbdbd" borderRadius={1} p={3} textAlign="center" mb={2}>
            <input
              type="file"
              accept=".pdf"
              id="archivoComunicacion-upload"
              style={{ display: "none" }}
              onChange={handleFileChange}
              disabled={fileUploading}
            />
            <label htmlFor="archivoComunicacion-upload">
              <Button
                component="span"
                variant="contained"
                startIcon={<UploadFileIcon />}
                disabled={fileUploading}
                sx={{ mb: 1 }}
              >
                Seleccionar Archivo
              </Button>
            </label>

            {uploadFile && (
              <Box mt={2}>
                <SuiTypography variant="body2" color="primary">
                  Archivo: {uploadFile.name}
                </SuiTypography>
              </Box>
            )}
          </Box>
          {tipoArchivo === "comprobante_envio" && (
            <SuiTypography variant="caption" color="textSecondary">
              El archivo debe ser un documento que compruebe el envío físico al destinatario (guía
              de envío, comprobante de empresa de mensajería, etc).
            </SuiTypography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUploadDialog(false)} disabled={fileUploading} color="error">
            Cancelar
          </Button>
          <SuiButton
            variant="gradient"
            buttonColor="dark"
            disabled={!uploadFile || fileUploading}
            onClick={handleUploadArchivo}
          >
            {fileUploading ? "Subiendo..." : "Subir Archivo"}
          </SuiButton>
        </DialogActions>
      </Dialog>
    </SuiBox>
  );
};

export default ComunicacionesTable;
